import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {
  MenuDeleteResp,
  MenuDeleteVariables,
  Menu
} from "../../types/menuTypes";

const DELETE_MENU = gql`
  mutation DeleteMenu($attributes: DeleteMenuInput!) {
    deleteMenu(attributes: $attributes) {
      menu {
        id
        name
      }
      errors
    }
  }
`   

type UseDeleteMenuOptions = {
  onCompleted?: (menu: Menu) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<MenuDeleteResp, MenuDeleteVariables>>[1];
};

const useDeleteMenu = (options?: UseDeleteMenuOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    MenuDeleteResp,
    MenuDeleteVariables
  >(DELETE_MENU, options?.mutationOptions);

  const deleteMenu = useCallback(
    async (input: MenuDeleteVariables['attributes']) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              id: input.id!
            },
          },
        });

        const deleteMenu = result?.data?.deleteMenu;

        if (deleteMenu?.errors?.length) {
          throw new Error(deleteMenu.errors.join(", "));
        }

        if (options?.onCompleted && deleteMenu?.menu) {
          options.onCompleted(deleteMenu.menu);
        }

        return deleteMenu?.menu;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const menu = data?.deleteMenu?.menu;

  return {
    deleteMenu,
    deletedMenu: menu,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useDeleteMenu
